import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, BehaviorSubject, of, throwError, from } from 'rxjs';
import { catchError, map, tap, finalize, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { marked } from 'marked';

export type MessageType = 'user' | 'ai' | 'code' | 'error';

export interface ChatMessage {
  type: MessageType;
  content: string;
  files: any | null;
  formattedContent: string;
  timestamp: Date | null;
}

export interface ChatApiResponse {
  message: string;
  showCloseChatButton: boolean;
  files: any | null;
  isCode: boolean;
  threadId: string;
  assistantId: string;
}

@Injectable({
  providedIn: 'root',
})
export class ChatService {
  private readonly apiUrl: string;
  private readonly messages: ChatMessage[] = [];
  private readonly messagesSubject = new BehaviorSubject<ChatMessage[]>([]);
  private currentThreadId: string | null = null;

  public readonly messages$ = this.messagesSubject.asObservable();

  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthService
  ) {
    this.apiUrl = `${environment.BACKEND_URL}/ssl-chat`;

    marked.setOptions({
      gfm: true,
      breaks: true,
    });
  }

  postMessage(message: string | null) {
    message = message?.trim() ?? '';
    // for consequent messaged
    if (this.currentThreadId && !message) {
      return throwError(() => new Error('Message cannot be empty'));
    }
    if (this.currentThreadId) {
      const userChat: ChatMessage = {
        type: 'user',
        files: null,
        content: message,
        formattedContent: message,
        timestamp: new Date(),
      };

      this.updateMessageList(userChat);
    }
    const payload = {
      message,
      threadId: this.currentThreadId,
    };

    return this.http.post(this.apiUrl, payload).pipe(
      tap((response: any) => {
        let msg: ChatMessage = {
          type: 'ai',
          content: response.message ?? response.error,
          timestamp: new Date(),
          formattedContent: '',
          files: response.files || {},
        };
        this.currentThreadId = response?.threadId;
        this.updateMessageList(msg);
      })
    );
  }

  private updateMessageList(newMessage: ChatMessage): void {
    this.messages.push(newMessage);
    this.messagesSubject.next([...this.messages]);
  }

  private handleError(error: Error): Observable<void> {
    console.error('Chat service error:', error);

    const errorMessage: ChatMessage = {
      type: 'error',
      files: null,
      content:
        error.message ||
        'An unexpected error occurred. Please try again later.',
      formattedContent:
        error.message ||
        'An unexpected error occurred. Please try again later.',
      timestamp: new Date(),
    };

    this.updateMessageList(errorMessage);
    return throwError(() => error);
  }

  public clearChat(): void {
    this.messages.length = 0;
    this.currentThreadId = null;
    this.messagesSubject.next([]);
  }

  public reloadChat(chatId: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/reload/${chatId}`).pipe(
      tap((response: any) => {
        console.log('Reloading chat with ID:', chatId);
        if (response.success) {
          // Clear existing messages
          this.messages.length = 0;

          // Set the thread ID
          this.currentThreadId = response.chat.thread_id;

          // Convert OpenAI messages to ChatMessage format
          const chatMessages: ChatMessage[] = response.messages
            .map((msg: any) => {
              // const content = msg.content[0]?.text?.value || '';
              const content = msg.message || '';
              return {
                type: msg.role === 'user' ? 'user' : 'ai',
                content: content,
                formattedContent: content,
                timestamp: new Date(msg.timestamp * 1000),
                files: null
              };
            })
            .reverse(); // Reverse to show oldest first

          // Update messages
          this.messages.push(...chatMessages);
          this.messagesSubject.next([...this.messages]);
        }
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('Error reloading chat:', error);
        return throwError(() => new Error(error.error?.error || 'Failed to reload chat'));
      })
    );
  }

  // TODO: Still half implemented
  public initiateChat(): Observable<any> {
    return this.http.get(`${this.apiUrl}/initiate`).pipe(
      tap((response: any) => {
        if (response.success) {
          // Clear existing messages
          this.messages.length = 0;

          // Set the thread ID
          this.currentThreadId = response.chat.thread_id;

          // Convert OpenAI messages to ChatMessage format
          const chatMessages: ChatMessage[] = response.messages
            .map((msg: any) => {
              const content = msg.content[0]?.text?.value || '';
              return {
                type: msg.role === 'user' ? 'user' : 'ai',
                content: content,
                formattedContent: content,
                timestamp: new Date(msg.created_at * 1000),
                files: null
              };
            })
            .reverse(); // Reverse to show oldest first

          // Update messages
          this.messages.push(...chatMessages);
          this.messagesSubject.next([...this.messages]);
        }
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('Error reloading chat:', error);
        return throwError(() => new Error(error.error?.error || 'Failed to reload chat'));
      })
    );
  }
}
